import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SUPPORT_EMAIL, MAILTO_SUPPORT_HREF } from '../../constants/publicSite';

type ForgotPasswordModalProps = {
  open: boolean;
  onClose: () => void;
  /** `/admin` sign-in uses platform-oriented copy; `/login` uses workspace user copy */
  variant: 'workspace' | 'platform';
};

export default function ForgotPasswordModal({ open, onClose, variant }: ForgotPasswordModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const body =
    variant === 'platform' ? (
      <>
        <p className="text-sm leading-relaxed text-gray-600">
          Platform administrator accounts do not use self-service password reset. If you need access or a password
          change, contact ParachuteIQ support.
        </p>
      </>
    ) : (
      <>
        <p className="text-sm leading-relaxed text-gray-600">
          ParachuteIQ sign-in uses organization-managed accounts. Your law firm or company administrator can issue a
          password reset link from the workspace user management screens (same flow as when you were invited).
        </p>
        <p className="mt-3 text-sm leading-relaxed text-gray-600">
          If you are unsure who that is, or you still cannot sign in, contact support and include your work email.
        </p>
      </>
    );

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="forgot-password-title"
      onClick={onClose}
    >
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          aria-label="Close"
        >
          <span className="text-xl leading-none">×</span>
        </button>
        <h2 id="forgot-password-title" className="pr-10 text-lg font-semibold text-gray-900">
          Forgot your password?
        </h2>
        <div className="mt-3">{body}</div>
        <p className="mt-4 text-sm text-gray-600">
          Email:{' '}
          <a href={MAILTO_SUPPORT_HREF} className="font-medium text-emerald-700 underline-offset-2 hover:underline">
            {SUPPORT_EMAIL}
          </a>
        </p>
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
